/**
 * The preview frame's documents (PF1): a /workspace HTML page the assistant shows, run in an
 * `<iframe sandbox="allow-scripts">` (an opaque origin: no cookies, no storage of ours, no access
 * to the app). The page's srcdoc is our CSP first, then our bootstrap, then the page, so the
 * policy is in force before a byte of the page is parsed and the bootstrap runs before its scripts.
 *
 * The CSP lets nothing reach the network: no connect, no remote images, fonts, styles or media,
 * no frames, forms or base. Scripts are the page's inline ones ('unsafe-inline', so no nonce: a
 * nonce or hash would switch it off) and our script mirror (the Pyodide index the sandbox frame
 * already loads from). The page is untrusted as a whole, so inline scripts add no power it lacks;
 * what holds it is the opaque origin, this policy and the wrapper.
 *
 * A document's own CSP does not govern where its frame navigates; the parent's frame-src does. So
 * the page's frame sits in a wrapper of ours (another sandboxed srcdoc, under the same policy):
 * its frame-src 'none' refuses every navigation of the page's frame (http, blob:, data:, the app's
 * own), whatever the app's page allows, while the srcdoc itself still loads. The wrapper relays
 * a port, once per page document, to the page that asks with this load's token: the READY it
 * passes up carries the document's number, and only a port the app sends back with that number
 * goes down.
 *
 * When the page's frame leaves our document anyway (about:blank is not fetched, so frame-src does
 * not refuse it; a refused navigation shows an error page), the wrapper puts our srcdoc back.
 *
 * The bootstrap gives the page `steddy.fs` (read, list, stat: read-only) and a `fetch()` for
 * relative URLs, both answered over a MessagePort by the tab that runs the workspace (port.ts).
 * It also keeps the page to its one window: no WebRTC in it, and no frame, whose window would be
 * one the bootstrap never ran in. sandbox.md has the whole design.
 */

import { PAGE_MAX_BYTES } from "./port";

/** The page's own messages to the app, and the app's answer carrying the port. */
export const READY_MESSAGE = "steddy-preview-ready";
export const PORT_MESSAGE = "steddy-preview-port";
/** The page's word to the wrapper that its load finished (the load counted as ours). */
export const LOADED_MESSAGE = "steddy-preview-loaded";
/** The wrapper's word to the app that it removed the page's frame: its port is spent. */
export const GONE_MESSAGE = "steddy-preview-gone";

/**
 * How long the wrapper waits, after the page's frame loads, for the page's LOADED (which may come
 * just after the load event) before it takes the load as the page leaving.
 */
const LOADED_WAIT_MS = 1000;

/** How long a worker's wrapped script URL lives when the worker sends nothing. */
const WORKER_URL_MS = 30_000;

/** Resource hints a browser may act on before any CSP check (a preconnect leaks its host name). */
const HINTS = [
  "preconnect",
  "dns-prefetch",
  "prefetch",
  "prerender",
  "preload",
  "modulepreload",
  "next",
];
const HINT_SELECTOR = [
  ...HINTS.map((rel) => `link[rel~="${rel}" i]`),
  'script[type="speculationrules" i]',
].join(", ");
/** Elements that would give the page another window (a srcdoc, about:blank or javascript: one). */
const FRAME_SELECTOR = "iframe, frame";
/** What the bootstrap removes as the page adds it. */
const ADDED_SELECTOR = `${FRAME_SELECTOR}, ${HINT_SELECTOR}`;
/** A template's attributes that make it a declarative shadow root (the second, Chromium's old one). */
const DECLARATIVE_SHADOW = ["shadowrootmode", "shadowroot"];
/** How often the wrapper puts our srcdoc back after the page's frame left it; then the frame goes. */
const PAGE_RELOADS = 3;

/**
 * A CSP source for a folder URL (origin plus a path ending in "/", so it matches only what is
 * under it), or null for anything but http(s), a path that is not a folder, or one with an
 * encoded dot or slash (a browser may decode those before matching).
 */
export function cspSource(value: string, base?: string): string | null {
  if (/%2e|%2f|%5c/i.test(value)) return null;
  try {
    const url = new URL(value, base);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    if (!url.pathname.endsWith("/")) return null;
    return `${url.origin}${url.pathname}`.replace(/[;,\s'"]/g, "");
  } catch {
    return null;
  }
}

/**
 * The frame's policy. `scripts` are the remote prefixes scripts may also come from (our mirror);
 * blob: and data: are local, made by the page itself. `webrtc 'block'` stops peer connections
 * where the browser knows the directive; elsewhere the bootstrap removes the constructors.
 * Chromium logs it as unrecognized; it stays for the documents the bootstrap never ran in
 * (sandbox.md).
 * Trusted Types are required for scripts so that a javascript: navigation asks the bootstrap's
 * default policy first, which refuses it (see bootstrap; tested for location and
 * window.open(_self) in WebKit); it passes everything else as it is.
 */
export function previewCsp(scripts: readonly string[] = []): string {
  const remote = scripts.map((value) => cspSource(value)).filter((value) => value !== null);
  return [
    "default-src 'none'",
    ["script-src 'unsafe-inline' 'unsafe-eval' 'wasm-unsafe-eval' blob:", ...remote].join(" "),
    "style-src 'unsafe-inline' blob: data:",
    "img-src blob: data:",
    "font-src blob: data:",
    "media-src blob: data:",
    "connect-src blob: data:",
    "worker-src blob:",
    "frame-src 'none'",
    "child-src 'none'",
    "object-src 'none'",
    "manifest-src 'none'",
    "form-action 'none'",
    "base-uri 'none'",
    "webrtc 'block'",
    "require-trusted-types-for 'script'",
  ].join("; ");
}

/** What the page's markup may not keep: hints, frames and `<noscript>` (see cleanPage). */
const STRIPPED = `noscript, ${FRAME_SELECTOR}, ${HINT_SELECTOR}`;
/** Parses and serialisations until the page is stable; past that it is refused. */
const ROUNDS = 3;
/** Markup parsed for one page, every round included; past that it is refused. */
export const PARSE_BUDGET_CHARS = 4 * PAGE_MAX_BYTES;
/** How the frame parses the page: after a doctype, in standards mode (a srcdoc document is too). */
const STANDARDS = "<!doctype html>";

export class UnstablePageError extends Error {
  constructor(message = "The page's markup changes each time it is parsed") {
    super(message);
  }
}

function strip(root: ParentNode) {
  root.querySelectorAll(STRIPPED).forEach((node) => node.remove());
  for (const template of root.querySelectorAll("template")) {
    for (const name of DECLARATIVE_SHADOW) template.removeAttribute(name);
    strip((template as HTMLTemplateElement).content);
  }
}

/**
 * The page as the frame may parse it (parsed inertly here, never run), always as we serialised
 * it: the frame parses what we checked, never the raw page. Each parse is in standards mode, as
 * the frame's is. Gone, in templates too:
 * - resource hints, which a browser may act on before any CSP check;
 * - frames, whose windows the bootstrap never runs in (it removes those a script adds);
 * - declarative shadow roots (the templates stay, inert): a closed one would hide what the page
 *   puts in it from the bootstrap, which sees only the roots attachShadow makes;
 * - `<noscript>`: parsed here with scripting off its content is markup, in the frame with
 *   scripting on it is text, so what is inside it could differ between the two parses.
 * A page whose serialisation does not settle (markup that parses differently once written out),
 * or that needs more than `budget` characters of parsing, is refused with UnstablePageError.
 */
export function cleanPage(html: string, budget = PARSE_BUDGET_CHARS): string {
  let current = html;
  for (let round = 0; round < ROUNDS; round++) {
    budget -= current.length;
    if (budget < 0) throw new UnstablePageError("The page's markup is too large to check");
    const parsed = new DOMParser().parseFromString(STANDARDS + current, "text/html");
    strip(parsed);
    const out = parsed.documentElement.outerHTML;
    if (out === current) return out;
    current = out;
  }
  throw new UnstablePageError();
}

/** This load's token: the page's READY carries it, and the port goes only to a READY with it. */
export function previewToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function attribute(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

function inlineScript(source: string): string {
  return `<script>${source.replace(/<\//g, "<\\/")}</script>`;
}

/**
 * Runs first in the frame. It asks the app for the port, then gives the page:
 * - `steddy.fs.read(path)` (the file's text), `list(path = ".")`, `stat(path)`: paths relative
 *   to the page's folder, or absolute under /workspace;
 * - `fetch(url)` for a relative URL (no scheme, not starting with "/"): GET or HEAD only, answered
 *   from the workspace (404 when the file is missing); anything else goes to the browser's own
 *   fetch, which the CSP refuses.
 * It also refuses javascript: navigations (through Trusted Types), removes WebRTC, and removes the
 * frames and resource hints a script adds later (see below). When the page's own load ends it says
 * so to the wrapper (LOADED), which tells that load from one of a page that left.
 */
const bootstrap = (token: string) => String.raw`(() => {
  const READY = "${READY_MESSAGE}";
  const PORT = "${PORT_MESSAGE}";
  const LOADED = "${LOADED_MESSAGE}";
  const TOKEN = "${token}";
  // The page may replace window.parent; the wrapper's window is taken now.
  const up = parent;
  let port = null;
  let next = 1;
  const queued = [];
  const pending = new Map();
  // A javascript: URL in this frame would run, and WebKit makes the string it returns a document
  // in a new window: one with WebRTC, which this script never ran in. The CSP requires Trusted
  // Types, so a javascript: navigation first asks the default policy, for its "Location href"
  // sink, and gets null: tested for location and window.open(_self) in WebKit (a link and a
  // refresh ran no javascript: URL there even without this frame, so they are untested).
  // Everything else passes as it is. The policy is made before any of the page's scripts, and
  // there can be only one default.
  const NAVIGATION_SINK = "Location href";
  const same = (value) => value;
  if (window.trustedTypes) {
    trustedTypes.createPolicy("default", {
      createHTML: same,
      createScriptURL: same,
      createScript: (value, type, sink) => (sink === NAVIGATION_SINK ? null : value),
    });
  }
  // A worker keeps the CSP but not the policy, so its eval, importScripts and string timers would
  // fail. Each blob: worker (the only ones worker-src allows) runs its script after a pass-through
  // policy of its own: a worker cannot navigate. A script that cannot be read synchronously gets
  // the plain worker. A script that starts with a "use strict" directive (after comments and
  // other directives) gets one before the policy, so it stays strict. The new URL is revoked
  // once the worker speaks, or after a while: WebKit may load the script after a revoke.
  const NativeWorker = window.Worker;
  const WORKER_POLICY = 'if (self.trustedTypes) trustedTypes.createPolicy("default", { createHTML: (v) => v, createScript: (v) => v, createScriptURL: (v) => v });' + "\n";
  const STRICT = /^(?:\s|\/\/[^\n]*\n|\/\*(?:[^*]|\*(?!\/))*\*\/|(["'])[^"'\\\n]*\1\s*;)*(["'])use strict\2/;
  const workerSource = (url) => {
    if (!/^blob:/i.test(url)) return null;
    try {
      const request = new XMLHttpRequest();
      request.open("GET", url, false);
      request.send();
      return request.status === 200 ? request.responseText : null;
    } catch (e) {
      return null;
    }
  };
  if (window.trustedTypes && NativeWorker) {
    const Worker = function Worker(url, options) {
      if (!new.target) throw new TypeError("Constructor Worker requires 'new'");
      const source = workerSource(String(url));
      if (source === null) return Reflect.construct(NativeWorker, options === undefined ? [url] : [url, options], new.target);
      const prelude = (STRICT.test(source) ? '"use strict"; ' : "") + WORKER_POLICY;
      const script = URL.createObjectURL(new Blob([prelude + source], { type: "text/javascript" }));
      const worker = Reflect.construct(NativeWorker, options === undefined ? [script] : [script, options], new.target);
      let revoked = false;
      const revoke = () => {
        if (revoked) return;
        revoked = true;
        URL.revokeObjectURL(script);
      };
      worker.addEventListener("message", revoke, { once: true });
      worker.addEventListener("error", revoke, { once: true });
      setTimeout(revoke, ${WORKER_URL_MS});
      return worker;
    };
    Worker.prototype = NativeWorker.prototype;
    Object.defineProperty(window, "Worker", { value: Worker, writable: true, configurable: true });
  }
  // WebRTC's ICE gathering reaches STUN servers over UDP, which no CSP these engines enforce
  // governs: it goes from this window, and the page may have no other (below).
  for (const name of ["RTCPeerConnection", "webkitRTCPeerConnection", "RTCDataChannel", "RTCIceCandidate", "RTCSessionDescription"]) {
    try { Object.defineProperty(window, name, { value: undefined, writable: false, configurable: false }); } catch (e) {}
  }
  // A frame the page adds would be a window this script never ran in: a srcdoc or about:blank is
  // not fetched, so frame-src does not stop it. Each goes as it is added (an observer runs in this
  // task's microtasks, a srcdoc loads in a later task), in the document and in every shadow root,
  // which attachShadow makes and so is watched: the page's markup has no declarative ones
  // (cleanPage), and the parsers that make them are replaced. Resource hints go the same way.
  // Natives are taken now, called through Reflect.apply: the page may replace any prototype's.
  const apply = Reflect.apply;
  const getter = (target, name) => Object.getOwnPropertyDescriptor(target, name).get;
  const nodeType = getter(Node.prototype, "nodeType");
  const addedNodes = getter(MutationRecord.prototype, "addedNodes");
  const listLength = getter(NodeList.prototype, "length");
  const matches = Element.prototype.matches;
  const remove = Element.prototype.remove;
  const selectInElement = Element.prototype.querySelectorAll;
  const selectInDocument = Document.prototype.querySelectorAll;
  const selectInFragment = DocumentFragment.prototype.querySelectorAll;
  const observe = MutationObserver.prototype.observe;
  const nativeAttachShadow = Element.prototype.attachShadow;
  const Parser = DOMParser;
  const parseFromString = DOMParser.prototype.parseFromString;
  const added = ${JSON.stringify(ADDED_SELECTOR)};
  const removeAll = (list) => {
    for (let i = 0, length = apply(listLength, list, []); i < length; i++) apply(remove, list[i], []);
  };
  const sweep = (node) => {
    const type = apply(nodeType, node, []);
    if (type === 1 && apply(matches, node, [added])) apply(remove, node, []);
    else if (type === 1) removeAll(apply(selectInElement, node, [added]));
    else if (type === 9) removeAll(apply(selectInDocument, node, [added]));
    else if (type === 11) removeAll(apply(selectInFragment, node, [added]));
  };
  const observer = new MutationObserver((records) => {
    for (let i = 0; i < records.length; i++) {
      const nodes = apply(addedNodes, records[i], []);
      for (let j = 0, length = apply(listLength, nodes, []); j < length; j++) sweep(nodes[j]);
    }
  });
  const watch = (root) => {
    apply(observe, observer, [root, { __proto__: null, childList: true, subtree: true }]);
    sweep(root);
  };
  watch(document);
  const replace = (target, name, value) => {
    if (Object.prototype.hasOwnProperty.call(target, name)) Object.defineProperty(target, name, { value, writable: true, configurable: true });
  };
  replace(Element.prototype, "attachShadow", function attachShadow(init) {
    // Never clonable: a clone's shadow root is made without attachShadow.
    const root = apply(nativeAttachShadow, this, [{ ...init, clonable: false }]);
    watch(root);
    return root;
  });
  // The plain forms of the parsers that make declarative shadow roots; those with none (the
  // sanitizing ones, and document.write, which feeds the page's own parser) are not offered.
  const setHTMLUnsafe = function setHTMLUnsafe(html) { this.innerHTML = html; };
  replace(Element.prototype, "setHTMLUnsafe", setHTMLUnsafe);
  replace(ShadowRoot.prototype, "setHTMLUnsafe", setHTMLUnsafe);
  replace(Document, "parseHTMLUnsafe", function parseHTMLUnsafe(html) { return apply(parseFromString, new Parser(), [String(html), "text/html"]); });
  const unavailable = (name) => function () { throw new DOMException(name + " is not available in a preview", "NotSupportedError"); };
  replace(Element.prototype, "setHTML", unavailable("setHTML"));
  replace(ShadowRoot.prototype, "setHTML", unavailable("setHTML"));
  replace(Document, "parseHTML", unavailable("Document.parseHTML"));
  for (let target = Object.getPrototypeOf(document); target; target = Object.getPrototypeOf(target)) {
    replace(target, "write", unavailable("document.write"));
    replace(target, "writeln", unavailable("document.writeln"));
  }
  const failure = (error) => {
    const out = new Error((error && error.message) || "The workspace could not be read");
    out.code = (error && error.code) || "EINVAL";
    return out;
  };
  const call = (op, path) => new Promise((resolve, reject) => {
    const id = next++;
    pending.set(id, { resolve, reject });
    const message = { id, op, path: String(path) };
    if (port) port.postMessage(message);
    else queued.push(message);
  });
  addEventListener("message", (event) => {
    const data = event.data;
    if (port || event.source !== up || !data || data.type !== PORT || data.token !== TOKEN || !event.ports[0]) return;
    port = event.ports[0];
    port.onmessage = (answer) => {
      const reply = answer.data || {};
      const waiting = pending.get(reply.id);
      if (!waiting) return;
      pending.delete(reply.id);
      if (reply.ok) waiting.resolve(reply.result);
      else waiting.reject(failure(reply.error));
    };
    for (const message of queued.splice(0)) port.postMessage(message);
  });
  const fs = Object.freeze({
    read: (path) => call("read", path),
    list: (path = ".") => call("list", path),
    stat: (path) => call("stat", path),
  });
  Object.defineProperty(window, "steddy", { value: Object.freeze({ fs }), writable: false, configurable: false });
  const nativeFetch = window.fetch.bind(window);
  const relative = (url) => !/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url) && !/^[\\/]/.test(url);
  window.fetch = function fetch(input, init) {
    const url = typeof input === "string" ? input : null;
    if (url === null || !relative(url)) return nativeFetch(input, init);
    const method = String((init && init.method) || "GET").toUpperCase();
    if (method !== "GET" && method !== "HEAD") return Promise.reject(new TypeError("The workspace is read-only"));
    let path;
    try { path = decodeURIComponent(url.split(/[?#]/)[0]); } catch (e) { return Promise.reject(new TypeError("Bad URL: " + url)); }
    return call("fetch", path).then(
      (file) => new Response(method === "HEAD" ? null : file.bytes, { status: 200, headers: { "content-type": file.type } }),
      (error) => {
        if (error.code === "ENOENT" || error.code === "EISDIR") return new Response("Not found", { status: 404 });
        throw new TypeError(error.message);
      }
    );
  };
  addEventListener("load", () => up.postMessage({ type: LOADED, token: TOKEN }, "*"), true);
  up.postMessage({ type: READY, token: TOKEN }, "*");
})();`;

/**
 * Runs in the wrapper: the page's READY (from its own frame, with the token) goes up to the app
 * once per document of ours in the page's frame, with that document's number; the port the app
 * answers with goes down to the page once, and only when it carries the current number. A port
 * for a document that is gone is closed: the page now in the frame waits for its own. Anything
 * else is ignored.
 *
 * The page's frame loads our srcdoc, then anything else only by leaving it: a navigation this
 * frame-src refused (Chromium shows an error page) or one it does not govern (about:blank), into
 * a window the bootstrap never ran in. A load is ours only when the page's LOADED (posted from its
 * own load) comes before it, or at most LOADED_WAIT_MS after it: Chromium and WebKit both fire
 * the frame's load before the page's message arrives. Any other load is the page leaving, before
 * its load or after it. Each time a fresh copy of the frame (same srcdoc, a new browsing context)
 * takes its place, PAGE_RELOADS times; then the frame goes, and the app is told its port is spent
 * (GONE).
 */
const relay = (token: string) => String.raw`(() => {
  const READY = "${READY_MESSAGE}";
  const PORT = "${PORT_MESSAGE}";
  const LOADED = "${LOADED_MESSAGE}";
  const GONE = "${GONE_MESSAGE}";
  const TOKEN = "${token}";
  const pageFrame = () => document.querySelector("iframe[data-agent-preview-page]");
  // The document of ours now in the page's frame: its number, whether it asked for a port and was
  // given one, whether its LOADED came, and whether its load was counted (or is awaited).
  let doc = 0;
  let asked = false;
  let given = false;
  let loaded = false;
  let counted = false;
  let waiting = 0;
  let reloads = 0;
  const leave = () => {
    const frame = pageFrame();
    if (!frame) return;
    clearTimeout(waiting);
    doc += 1;
    asked = false;
    given = false;
    loaded = false;
    counted = false;
    waiting = 0;
    if (reloads++ < ${PAGE_RELOADS}) {
      frame.replaceWith(frame.cloneNode(false));
    } else {
      const note = document.createElement("p");
      note.textContent = "This page tried to leave the preview, so it was stopped.";
      frame.replaceWith(note);
      parent.postMessage({ type: GONE, token: TOKEN }, "*");
    }
  };
  addEventListener("message", (event) => {
    const data = event.data;
    if (!data || typeof data !== "object" || data.token !== TOKEN) return;
    const frame = pageFrame();
    const page = frame && frame.contentWindow;
    if (event.source === parent && data.type === PORT && event.ports[0]) {
      if (page && asked && !given && data.doc === doc) {
        given = true;
        page.postMessage({ type: PORT, token: TOKEN }, "*", [event.ports[0]]);
      } else {
        event.ports[0].close();
      }
      return;
    }
    if (!page || event.source !== page) return;
    if (data.type === READY && !asked) {
      asked = true;
      parent.postMessage({ type: READY, token: TOKEN, doc }, "*");
    } else if (data.type === LOADED) {
      loaded = true;
      if (waiting) {
        clearTimeout(waiting);
        waiting = 0;
        counted = true;
      }
    }
  });
  // Load events do not reach the window: caught on the document, as they pass it.
  document.addEventListener("load", (event) => {
    const frame = pageFrame();
    if (!frame || event.target !== frame) return;
    if (counted || waiting) leave();
    else if (loaded) counted = true;
    else waiting = setTimeout(leave, ${LOADED_WAIT_MS});
  }, true);
})();`;

function head(scripts: readonly string[]): string {
  return (
    `<!doctype html><html><head><meta charset="utf-8">` +
    `<meta http-equiv="Content-Security-Policy" content="${attribute(previewCsp(scripts))}">` +
    `<meta http-equiv="x-dns-prefetch-control" content="off">` +
    `<meta name="referrer" content="no-referrer">`
  );
}

/** The page's srcdoc: our CSP, then the bootstrap, then the page. */
export function previewDocument(
  html: string,
  scripts: readonly string[] = [],
  token = previewToken()
): string {
  return `${head(scripts)}${inlineScript(bootstrap(token))}</head>${cleanPage(html)}`;
}

/**
 * The wrapper's srcdoc, which the app frames: the same CSP (the page's srcdoc inherits it too),
 * the relay, and the page's own sandboxed frame filling it.
 */
export function previewWrapper(
  html: string,
  scripts: readonly string[] = [],
  token = previewToken()
): string {
  const page = previewDocument(html, scripts, token);
  return (
    `${head(scripts)}<style>html,body{margin:0;height:100%;overflow:hidden}` +
    `iframe{display:block;width:100%;height:100%;border:0;background:#fff}` +
    `p{margin:0;padding:12px 14px;font:13px system-ui,sans-serif;color:#555;background:#fff}</style>` +
    `${inlineScript(relay(token))}</head><body>` +
    `<iframe sandbox="allow-scripts" referrerpolicy="no-referrer" title="Page" ` +
    `data-agent-preview-page srcdoc="${attribute(page)}"></iframe></body></html>`
  );
}
